import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { Message } from "../../features/chat/types/UIMessage";
import {
  useChatroomMessages,
  useChatroomParticipants,
  useInbox,
  usePinChat,
  useLeaveConversation,
  useRenameChatroom,
} from "../../features/chat";
import { chatApi } from "../../features/chat/api";
import { useInboxCachePatcher } from "./useInboxCachePatcher";
import {
  buildParticipantSummary,
  filterBySearch,
  inboxItemToConversation,
  resolveCurrentUserName,
  toAvatarLabel,
} from "../../utils/InboxHelper";
import {
  formatTimestamp,
  mapChatMessage,
  parseMessageData,
  hasAttachment,
} from "../../utils/MessageHelper";
import { useBoundStore } from "../../store";
import {
  useSocket,
  DEVICE_ID,
  emitUserJoinToRoomEvent,
  emitUserTypingEvent,
  emitUserSendMessage,
  emitLeaveChatRoomEvent,
  emitMessagePollingReceivedAckEvent,
  subscribeReceivedMessageEvent,
  subscribeUpdateMessageIdEvent,
  subscribeTypingEvent,
  subscribeLastUserActivityEvent,
  subscribeUpdateUserMessageReadStatusEvent,
  subscribeUpdateChatMessageDeleteStatusEvent,
  subscribeUpdateRenamegroupchat,
  type SocketRenamePayload,
} from "../../providers/socket/SocketProvider";
import {
  ARCHIVED_INBOX_REQUEST,
  CHAT_STRINGS,
  LEAVE_CONVERSATION_TYPE,
  TEAM_INBOX_REQUEST,
} from "./constants";
import type {
  ChatWindowViewModelState,
  ParticipantsDialogState,
  RenameDialogState,
  SocketDeletePayload,
  SocketMessagePayload,
  SocketReadStatusPayload,
  SocketUpdateIdPayload,
} from "./types";

export type { Conversation } from "./types";
export type { ChatWindowViewModelState } from "./types";

// Module-level (not component state/ref) so it survives this hook's component
// remounting — messagePollingData lives in SocketProvider context above the route,
// so navigating away and back must not reprocess the same already-handled event.
let lastProcessedPollingEventKey: string | null = null;

export function useChatWindowViewModel(): ChatWindowViewModelState {
  const navigate = useNavigate();
  const patchInboxCache = useInboxCachePatcher();
  const { mutate: pinChat } = usePinChat();
  const { mutate: leaveConversation } = useLeaveConversation();
  const { mutate: renameChatroom } = useRenameChatroom();

  const [selectedId, setSelectedId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [typing, setTyping] = useState<string>("");
  const [confirmDialog, setConfirmDialog] =
    useState<ChatWindowViewModelState["confirmDialog"]>(null);
  const [renameDialog, setRenameDialog] = useState<RenameDialogState | null>(
    null,
  );
  const [participantsDialog, setParticipantsDialog] =
    useState<ParticipantsDialogState | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);
  // Optimistic pin override: immediately reflects pin/unpin in the list before inbox refetch settles
  const [pinnedOverrides, setPinnedOverrides] = useState<
    Record<string, boolean>
  >({});
  // True only during the inbox refetch that follows a leave-conversation action
  const [inboxRefetching, setInboxRefetching] = useState(false);
  // True while waiting for a newly-created chatroom to appear in the inbox (shows sidebar skeleton)
  const [pendingChatroomLoading, setPendingChatroomLoading] = useState(false);
  // Mirrors mobile's updateThreadDetails — locally-read chatroom IDs hide the unread badge
  // even before server confirms. Maps chatroomId → true when user opens the conversation.
  const [locallyReadChatroomIds, setLocallyReadChatroomIds] = useState<Set<string>>(new Set());
  // Messages delivered via socket since the current chatroom was opened
  const [socketMessages, setSocketMessages] = useState<Message[]>([]);
  // Tracks message IDs deleted via socket — overrides both REST and socket messages in the combined list
  const [deletedMessageIds, setDeletedMessageIds] = useState<Set<string>>(new Set());
  // Mirrors mobile's updateMessageStatus mutation: timestamp marks the READ boundary for existing messages.
  // Using ISO timestamp (not a boolean) ensures new messages sent AFTER the read event are unaffected.
  const [readBeforeISO, setReadBeforeISO] = useState<string | null>(null);
  // Ref so updateMessageStatusLocal early-exit can read current value without stale closure
  const readBeforeISORef = useRef<string | null>(null);

  const effectiveSsoId = useBoundStore((state) => state.shared.ssoId);

  // Refs used in cleanup effects to avoid stale closures
  const socketRef = useRef(useSocket().socket);
  const selectedIdRef = useRef(selectedId);
  const prevSelectedIdRef = useRef<string>("");
  // Tracks the temp ID of the optimistic message waiting for server confirmation
  const pendingMsgIdRef = useRef<string | null>(null);
  // Guards the reconnect effect so it only fires once per reconnect event
  const lastHandledReconnectRef = useRef<number>(0);
  // Mirrors mobile's getMessages() — always has the latest combined messages for updateMessageStatusLocal
  const messagesRef = useRef<Message[]>([]);

  const {
    socket,
    citusUserInfo,
    citusConfigData,
    messagePollingData,
    socketReconnection,
  } = useSocket();

  // Keep refs in sync with latest values
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // ── Team inbox (non-archived staff conversations) — always fetched ─────────
  const {
    data: rawTeamItems = [],
    isLoading: teamLoading,
    error: teamError,
    refetch: refetchTeam,
  } = useInbox(TEAM_INBOX_REQUEST);

  // ── Archived inbox — always fetched so "All" tab can show combined list ────
  const {
    data: rawArchivedItems = [],
    isLoading: archLoading,
    error: archError,
    refetch: refetchArchived,
  } = useInbox(ARCHIVED_INBOX_REQUEST);

  const handleRetry = useCallback(() => {
    void refetchTeam();
    void refetchArchived();
  }, [refetchTeam, refetchArchived]);

  // Resolve current user's display name from inbox participants using SSO ID
  const currentUserName = useMemo(
    () => resolveCurrentUserName(rawTeamItems, effectiveSsoId),
    [rawTeamItems, effectiveSsoId],
  );

  // ── Map raw API items → UI Conversation objects ───────────────────────────
  const teamItems = useMemo(
    () =>
      rawTeamItems.map((item) => {
        const c = inboxItemToConversation(item, currentUserName);
        const id = String(item.chatroomId);
        // Apply optimistic pin override
        if (id in pinnedOverrides) {
          c.pinnedStatus = pinnedOverrides[id];
        }
        // Apply local unread override — mirrors mobile updateThreadDetails
        if (locallyReadChatroomIds.has(id)) {
          c.unread = false;
        }
        return c;
      }),
    [rawTeamItems, currentUserName, pinnedOverrides, locallyReadChatroomIds],
  );

  const archivedItems = useMemo(
    () =>
      rawArchivedItems.map((item) => {
        const c = inboxItemToConversation(item, currentUserName);
        const id = String(item.chatroomId);
        // Apply optimistic pin override
        if (id in pinnedOverrides) {
          c.pinnedStatus = pinnedOverrides[id];
        }
        // Apply local unread override — mirrors mobile updateThreadDetails
        if (locallyReadChatroomIds.has(id)) {
          c.unread = false;
        }
        return c;
      }),
    [rawArchivedItems, currentUserName, pinnedOverrides, locallyReadChatroomIds],
  );

  // "All" = team (active) + archived combined; server returns newest-first
  const allItems = useMemo(
    () => [...teamItems, ...archivedItems],
    [teamItems, archivedItems],
  );

  // ── Apply search filter per tab ───────────────────────────────────────────
  const allConversations = filterBySearch(allItems, searchQuery);
  const teamConversations = filterBySearch(teamItems, searchQuery);
  const archivedConversations = filterBySearch(archivedItems, searchQuery);

  // ── Selected conversation ─────────────────────────────────────────────────
  const selectedConversation = allItems.find((c) => c.id === selectedId);

  // ── Chatroom messages (API) ───────────────────────────────────────────────
  const { data: messagesData, isLoading: messagesLoading } =
    useChatroomMessages(selectedId, !!selectedId);

  // ── Chatroom participants (API) ───────────────────────────────────────────
  const { data: participants = [] } = useChatroomParticipants(
    selectedId,
    !!selectedId,
  );

  // Resolve current user's internal userId from participants via SSO ID
  // Mobile pattern: compare message.userId === citusUserInfo.userId (internal numeric ID)
  const currentInternalUserId = useMemo(() => {
    const match = participants.find((p) => p.externalUserId === effectiveSsoId);
    return match?.userId ?? 0;
  }, [participants, effectiveSsoId]);

  // Build header title from participants (excluding current user by userId) ──
  const participantsSummary = useMemo(
    () => buildParticipantSummary(participants, currentInternalUserId),
    [participants, currentInternalUserId],
  );

  // ── Map ChatMessage[] → Message[] for bubble components ──────────────────
  // REST messages form the base; socket messages are appended (deduplicated by id).
  // readAll override (set by updateUserMessageReadStatus) is applied across both,
  // mirroring mobile's updateMessageStatus() which iterates the full messages array.
  const messages = useMemo<Message[]>(() => {
    const restMessages = messagesData?.chatMessages
      ? messagesData.chatMessages
          .map((m) => mapChatMessage(m, currentInternalUserId))
          .reverse() // API returns newest-first; display oldest-first
      : [];
    const restIds = new Set(restMessages.map((m) => m.id));
    const uniqueSocketMessages = socketMessages.filter(
      (m) => !restIds.has(m.id),
    );
    const combined = [...restMessages, ...uniqueSocketMessages];
    // Apply deletion overrides from socket events — mirrors mobile's handleMessageDeletion on the combined list
    const withDeletions = deletedMessageIds.size > 0
      ? combined.map((m) => deletedMessageIds.has(m.id) ? { ...m, isDeleted: true, content: "" } : m)
      : combined;
    if (!readBeforeISO) return withDeletions;
    return withDeletions.map((m) => {
      if (m.type !== "sent" || !m.status || m.status === "read") return m;
      if (m.isoTimestamp && m.isoTimestamp <= readBeforeISO) {
        return { ...m, status: "read" as const };
      }
      return m;
    });
  }, [messagesData, socketMessages, currentInternalUserId, readBeforeISO, deletedMessageIds]);

  // Keep ref in sync after every render — mirrors mobile's getMessages() Zustand accessor pattern
  useEffect(() => {
    messagesRef.current = messages;
  });

  // Ref so socket handler can look up participant avatars without a stale closure
  const participantsRef = useRef<typeof participants>([]);
  useEffect(() => {
    participantsRef.current = participants;
  });

  // Mirrors mobile's updateMessageStatus() exactly: early exit + READ override via state
  const updateMessageStatusLocal = useCallback((lastUserActivity?: string) => {
    const msgs = messagesRef.current;
    if (msgs.length === 0) return;
    const newestMsg = msgs[msgs.length - 1];
    // Mobile early exit: newest msg already READ. Check ref so a second event after setting
    // the boundary is also skipped (prevents readBeforeISO being pushed forward by repeat events).
    const currentBoundary = readBeforeISORef.current;
    const newestEffectivelyRead =
      newestMsg.type === "received" ||
      newestMsg.status === "read" ||
      (!!currentBoundary &&
        !!newestMsg.isoTimestamp &&
        newestMsg.isoTimestamp <= currentBoundary);
    if (newestEffectivelyRead) return;
    // Capture timestamp boundary: messages with isoTimestamp <= readUpTo will show as READ.
    // New messages sent AFTER this moment have a later isoTimestamp and are unaffected.
    const readUpTo = lastUserActivity ?? new Date().toISOString();
    setReadBeforeISO(readUpTo);
    readBeforeISORef.current = readUpTo;
  }, []);

  // ── Socket: configure all listeners for the active chatroom ──────────────
  const configureSocketListeners = useCallback(
    (chatroomId: string) => {
      if (!socket || !citusUserInfo) return;

      const joinPayload = {
        userInfo: { ...citusUserInfo, roleId: citusUserInfo.roleId },
        roomId: chatroomId,
      };

      // Delete events are chatroom-scoped — guard by chatroomId
      subscribeUpdateChatMessageDeleteStatusEvent(
        socket,
        (deleteData: SocketDeletePayload) => {
          if (String(deleteData.chatroomId) !== String(chatroomId)) return;
          const msgId = String(deleteData.messageId);
          // Add to the override set so deletion is reflected in REST messages too
          setDeletedMessageIds((prev) => new Set([...prev, msgId]));
          // Also patch any in-flight socket message with the same id
          setSocketMessages((prev) =>
            prev.map((m) => m.id === msgId ? { ...m, isDeleted: true, content: "" } : m),
          );
        },
      );

      subscribeUpdateRenamegroupchat(
        socket,
        (nameUpdate: SocketRenamePayload) => {
          setGroupName(nameUpdate.chatHeading);
        },
      );

      // Mirrors mobile: updateMessageStatus(new Date(data * 1000).toISOString())
      subscribeLastUserActivityEvent(socket, (data: number) => {
        updateMessageStatusLocal(new Date(data * 1000).toISOString());
      });

      subscribeTypingEvent(socket, (data: string) => {
        setTyping(data);
      });

      // String() cast guards against server sending userid as string vs number
      subscribeUpdateUserMessageReadStatusEvent(
        socket,
        (data: SocketReadStatusPayload) => {
          if (String(data?.readUsers?.userid) === String(citusUserInfo?.userId))
            return;
          updateMessageStatusLocal();
        },
      );

      // Emit join — server will start delivering userMessage events for this room
      emitUserJoinToRoomEvent(
        socket,
        joinPayload,
        (data: Record<string, unknown>) => {
          console.log(`[Socket] joined chatroom ${chatroomId}`, data);
          // Mark received messages as read — mirrors mobile SocketProvider.tsx join callback
          void chatApi.updateMessageStatus(chatroomId);
        },
      );

      // Incoming message from server (both ours confirmed + others' messages)
      subscribeReceivedMessageEvent(
        socket,
        (messageData: SocketMessagePayload, displayName: string) => {
          const isSelf = citusUserInfo?.userId === messageData.userId;
          const msgIso = new Date(messageData.time * 1000).toISOString();
          // Admin/system messages (userId === 0) mirror the REST API mapChatMessage behaviour
          if (messageData.userId === 0) {
            const systemMsg: Message = {
              id: String(messageData.uniqueId || messageData.messageId),
              type: "system",
              content: messageData.data || messageData.message || "",
              timestamp: formatTimestamp(msgIso),
              isoTimestamp: msgIso,
            };
            setSocketMessages((prev) => {
              if (prev.some((m) => m.id === systemMsg.id)) return prev;
              return [...prev, systemMsg];
            });
            return;
          }
          const rawContent = messageData.data || messageData.message || "";
          const parsed = parseMessageData(rawContent);

          // Fall back to the avatar from the loaded participants list when socket omits it
          const participantAvatar = !isSelf
            ? participantsRef.current.find((p) => p.userId === messageData.userId)?.avatar
            : undefined;

          const newMsg: Message = {
            id: String(messageData.uniqueId || messageData.messageId),
            type: isSelf ? "sent" : "received",
            content: parsed.message || rawContent,
            calloutMessage: parsed.isCallout,
            calloutData:
              parsed.calloutData.length > 0 ? parsed.calloutData : undefined,
            hasAttachment: hasAttachment(rawContent),
            timestamp: formatTimestamp(msgIso),
            isoTimestamp: msgIso,
            userName: !isSelf ? displayName : undefined,
            avatarLabel: !isSelf ? toAvatarLabel(displayName) : undefined,
            avatarImage: !isSelf
              ? (messageData.avatar || participantAvatar || undefined)
              : undefined,
            status: isSelf ? "delivered" : undefined,
          };

          setSocketMessages((prev) => {
            if (isSelf) {
              // Server confirmed our optimistic message — update it in-place
              const idx = pendingMsgIdRef.current
                ? prev.findIndex((m) => m.id === pendingMsgIdRef.current)
                : -1;
              if (idx !== -1) {
                const updated = [...prev];
                updated[idx] = {
                  ...updated[idx],
                  id: newMsg.id,
                  status: "delivered",
                  timestamp: newMsg.timestamp,
                };
                pendingMsgIdRef.current = null;
                return updated;
              }
            }
            // Avoid duplicates (e.g. message already in REST history)
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
        },
      );

      // Server sends the permanent message ID to replace our temp ID
      subscribeUpdateMessageIdEvent(socket, (data: SocketUpdateIdPayload) => {
        setSocketMessages((prev) =>
          prev.map((m) =>
            m.id === String(data.unique) ? { ...m, id: String(data.id) } : m,
          ),
        );
      });
    },
    [socket, citusUserInfo, updateMessageStatusLocal],
  );

  // ── Socket: leave previous chatroom when selectedId changes ────────────────
  // Uses socketRef to avoid adding socket as a dep (which would cause re-fires)
  useEffect(() => {
    const prev = prevSelectedIdRef.current;
    prevSelectedIdRef.current = selectedId;
    if (prev && prev !== selectedId && socketRef.current) {
      emitLeaveChatRoomEvent(
        socketRef.current,
        prev,
        (data: Record<string, unknown>) => {
          console.log(`[Socket] left chatroom ${prev}`, data);
        },
      );
    }
  }, [selectedId]);

  // ── Socket: (re-)configure listeners when chatroom or socket identity changes
  useEffect(() => {
    if (!selectedId) return;
    configureSocketListeners(selectedId);
  }, [selectedId, configureSocketListeners]);

  // ── Socket: re-join on reconnect (mirrors ChatWindow.tsx useEffect in mobile)
  useEffect(() => {
    if (
      !socketReconnection.time ||
      socketReconnection.time === lastHandledReconnectRef.current ||
      !socket ||
      !selectedId ||
      !citusUserInfo
    )
      return;
    lastHandledReconnectRef.current = socketReconnection.time;
    console.log("[Socket] reconnecting — re-joining chatroom", selectedId);
    emitUserJoinToRoomEvent(
      socket,
      {
        userInfo: { ...citusUserInfo, roleId: citusUserInfo.roleId },
        roomId: selectedId,
      },
      () => {},
    );
  }, [socketReconnection.time, socket, selectedId, citusUserInfo]);

  // ── Socket: messagePolling — inbox thread update + ack ────────────────────
  // Mirrors both CommunicationsHome.tsx and ChatWindow.tsx useEffect([messagePollingData])
  // Reads selectedId via ref (not as a dependency) — closing/switching conversations must
  // NOT re-run this effect against a stale messagePollingData object, which previously
  // caused the just-read override to be incorrectly cleared (unread badge flash on close).
  useEffect(() => {
    if (!messagePollingData?.args) return;
    const { chatroomId: pollingRoomId, messageId, sentTime, message, chatroomArchived } =
      messagePollingData.args;
    // messagePollingData lives in SocketProvider context, above this route — it outlives
    // this component's mount. Without this guard, navigating away and back (e.g. to
    // add-participant) re-runs this effect against the SAME already-handled event on the
    // fresh mount, re-marking the conversation unread. Skip anything already processed.
    const eventKey = `${pollingRoomId}:${messageId}:${sentTime}`;
    if (eventKey === lastProcessedPollingEventKey) return;
    lastProcessedPollingEventKey = eventKey;
    const pollingRoomIdStr = String(pollingRoomId);

    // If polling is for a room that isn't currently open, clear its local-read override
    // so the unread badge can reappear. If polling is for the open room, keep it suppressed.
    // setTimeout defers setState out of the synchronous effect body (avoids cascading-renders warning)
    if (pollingRoomIdStr !== selectedIdRef.current) {
      setTimeout(() => {
        setLocallyReadChatroomIds((prev) => {
          const updated = new Set(prev);
          updated.delete(pollingRoomIdStr);
          return updated;
        });
      }, 0);
    }

    const patched = patchInboxCache(
      pollingRoomIdStr,
      {
        sentTime: new Date(Number(sentTime) * 1000).toISOString(),
        displayInfo3: message,
        lastMessage: message,
        unread: true,
      },
      { remove: !!chatroomArchived },
    );
    // Thread not found in either cached list (e.g. brand-new chatroom) — fall back
    // to a single silent refetch so it appears without showing the skeleton loader.
    if (!patched) {
      void refetchTeam();
      void refetchArchived();
    }

    if (socket && citusUserInfo) {
      emitMessagePollingReceivedAckEvent(socket, {
        userid: citusUserInfo.userId,
        displayName: citusUserInfo.displayName,
        chatroomId: pollingRoomId,
        messageId,
        uniqueDeviceId: DEVICE_ID,
      });
    }
  }, [messagePollingData, socket, citusUserInfo, patchInboxCache, refetchTeam, refetchArchived]);



  // ── Socket: leave active chatroom when component unmounts ─────────────────
  useEffect(() => {
    return () => {
      if (socketRef.current && selectedIdRef.current) {
        emitLeaveChatRoomEvent(
          socketRef.current,
          selectedIdRef.current,
          () => {},
        );
      }
    };
  }, []);

  // ── Send message via socket ───────────────────────────────────────────────
  const handleSendMessage = useCallback(
    (message: string) => {
      if (!socket || !citusUserInfo || !selectedId) return;

      const tempId = String(Math.floor(Math.random() * 1_000_000_000));
      const now = new Date();

      // Optimistic message shown immediately in the UI
      const optimistic: Message = {
        id: tempId,
        type: "sent",
        content: message,
        timestamp: formatTimestamp(now.toISOString()),
        isoTimestamp: now.toISOString(),
        status: "sent",
      };
      pendingMsgIdRef.current = tempId;
      setSocketMessages((prev) => [...prev, optimistic]);

      const payload = {
        messageInfo: {
          id: tempId,
          message,
          adminMessage: false,
          chatRoomId: selectedId,
          sentTime: String(now.getTime()),
          doubleVerificationStatus: false,
          language: "en",
          patientSiteId: 0,
        },
        userInfo: citusUserInfo,
        configData: citusConfigData,
      };

      emitUserTypingEvent(socket, "");
      emitUserSendMessage(socket, payload);

      // Own sent message doesn't trigger a messagePolling echo — patch the
      // inbox preview/order locally so the sidebar stays in sync immediately.
      patchInboxCache(selectedId, {
        sentTime: now.toISOString(),
        displayInfo3: message,
        lastMessage: message,
        unread: false,
      });
    },
    [socket, citusUserInfo, citusConfigData, selectedId, patchInboxCache],
  );

  // ── Typing event — mirrors handleTextInputChange in mobile ────────────────
  const handleTyping = useCallback(
    (text: string) => {
      if (!socket) return;
      emitUserTypingEvent(socket, text.length > 0 ? "typing..." : "");
    },
    [socket],
  );

  const clearConversationState = useCallback(() => {
    setSocketMessages([]);
    setDeletedMessageIds(new Set());
    setReadBeforeISO(null);
    readBeforeISORef.current = null;
    setTyping("");
    pendingMsgIdRef.current = null;
    setGroupName(null);
    setRenameDialog(null);
    setParticipantsDialog(null);
  }, []);

  const handleCloseConversation = useCallback(() => {
    setSelectedId("");
    clearConversationState();
    // Silent background sync on close — mirrors mobile's handleBackPress refetch,
    // self-heals any staleness (e.g. read-status) without showing the skeleton loader.
    void refetchTeam();
    void refetchArchived();
  }, [clearConversationState, refetchTeam, refetchArchived]);

  const handleMenuItemSelect = useCallback(
    (id: string) => {
      switch (id) {
        case "viewParticipants": {
          if (!selectedId) break;
          setParticipantsDialog({
            visible: true,
            onCancel: () => setParticipantsDialog(null),
          });
          break;
        }
        case "addParticipant": {
          if (!selectedId) break;
          navigate("add-participant", {
            state: {
              chatroomId: selectedId,
              externalSiteId: selectedConversation?.externalSiteId ?? null,
            },
          });
          break;
        }
        case "pinConversation": {
          if (!selectedId || !selectedConversation) break;
          const isPinned = selectedConversation.pinnedStatus;
          const label = isPinned
            ? CHAT_STRINGS.unpinConversationLabel
            : CHAT_STRINGS.pinConversationLabel;
          setConfirmDialog({
            visible: true,
            title: `${label}?`,
            confirmLabel: label,
            onConfirm: () => {
              pinChat(
                {
                  actionType: isPinned ? "unpin" : "pin",
                  chatRoomIds: [Number(selectedId)],
                },
                {
                  onSuccess: () => {
                    setPinnedOverrides((prev) => ({
                      ...prev,
                      [selectedId]: !isPinned,
                    }));
                    setConfirmDialog(null);
                  },
                  onError: () => setConfirmDialog(null),
                },
              );
            },
            onCancel: () => setConfirmDialog(null),
          });
          break;
        }
        case "renameGroup": {
          if (!selectedId) break;
          setRenameDialog({
            visible: true,
            currentName: groupName ?? selectedConversation?.chatroomName ?? "",
            onSave: (newName: string) => {
              renameChatroom(
                { chatRoomId: Number(selectedId), chatRoomName: newName },
                {
                  onSuccess: () => {
                    setGroupName(newName);
                    setRenameDialog(null);
                    void refetchTeam();
                  },
                  onError: () => setRenameDialog(null),
                },
              );
            },
            onCancel: () => setRenameDialog(null),
          });
          break;
        }
        case "leaveConversation": {
          if (!selectedId) break;
          setConfirmDialog({
            visible: true,
            title: CHAT_STRINGS.leaveConversationTitle,
            description: CHAT_STRINGS.leaveConversationDescription,
            confirmLabel: CHAT_STRINGS.leaveConversationLabel,
            confirmSeverity: "danger",
            onConfirm: () => {
              leaveConversation(
                {
                  chatroomId: Number(selectedId),
                  type: LEAVE_CONVERSATION_TYPE,
                },
                {
                  onSuccess: () => {
                    handleCloseConversation();
                    setConfirmDialog(null);
                    // Explicit skeleton refresh — the left conversation must disappear from the list
                    setInboxRefetching(true);
                    void Promise.all([refetchTeam(), refetchArchived()]).finally(() =>
                      setInboxRefetching(false),
                    );
                  },
                  onError: () => setConfirmDialog(null),
                },
              );
            },
            onCancel: () => setConfirmDialog(null),
          });
          break;
        }
        default:
          break;
      }
    },
    [
      selectedId,
      selectedConversation,
      groupName,
      pinChat,
      leaveConversation,
      renameChatroom,
      handleCloseConversation,
      refetchTeam,
      refetchArchived,
      navigate,
    ],
  );

  // ── Auto-open chatroom created via the create-chat flow ───────────────────
  const pendingNewChatroomId = useBoundStore(
    (state) => state.pendingNewChatroomId,
  );
  const pendingChatroomRefetchCountRef = useRef(0);

  useEffect(() => {
    if (!pendingNewChatroomId) {
      pendingChatroomRefetchCountRef.current = 0;
      setTimeout(() => setPendingChatroomLoading(false), 0);
      return;
    }
    const found = allItems.find((c) => c.id === pendingNewChatroomId);
    if (found) {
      setTimeout(() => {
        setSelectedId(pendingNewChatroomId);
        clearConversationState();
        useBoundStore.getState().setPendingNewChatroomId(null);
        // Silent background refresh so inbox reflects latest state (e.g. after add-participant)
        void refetchTeam();
      }, 0);
    } else if (pendingChatroomRefetchCountRef.current < 3) {
      // Room not in inbox yet (create-chat) — show skeleton and retry
      setTimeout(() => setPendingChatroomLoading(true), 0);
      pendingChatroomRefetchCountRef.current += 1;
      void refetchTeam();
    } else {
      // New room did not appear after retries — clear to avoid repeated refetches
      useBoundStore.getState().setPendingNewChatroomId(null);
    }
  }, [pendingNewChatroomId, allItems, clearConversationState, refetchTeam]);

  return {
    activeTabIndex,
    allConversations,
    teamConversations,
    archivedConversations,
    selectedId,
    selectedConversation,
    messages,
    messagesLoading,
    participants,
    participantsSummary,
    chatroomDetails: messagesData?.chatDetails,
    searchQuery,
    inboxLoading:
      teamLoading || archLoading || inboxRefetching || pendingChatroomLoading,
    inboxError: (teamError ?? archError) as Error | null,
    typing,
    isSocketReady: !!socket,
    onRetry: handleRetry,
    onTabChange: (index: number) => {
      setActiveTabIndex(index);
      handleCloseConversation();
    },
    selectConversation: (id: string) => {
      setSelectedId(id);
      // Mirrors mobile updateThreadDetails — optimistically hide unread badge
      setLocallyReadChatroomIds((prev) => new Set([...prev, id]));
      // Durable cache patch (not just the component-state override above) — survives
      // navigating away and back (e.g. add-participant) without an inbox refetch.
      patchInboxCache(id, { unread: false }, { moveToFront: false });
      clearConversationState();
    },
    closeConversation: handleCloseConversation,
    onMenuItemSelect: handleMenuItemSelect,
    onSearchChange: setSearchQuery,
    onSendMessage: handleSendMessage,
    onTyping: handleTyping,
    confirmDialog,
    renameDialog,
    participantsDialog,
    groupName,
  };
}
